import { v4 as uuidv4 } from 'uuid';

/**
 * REV-21798: identity vs. display for system-created reclassification accounts.
 *
 * When a user reclassifies onto a *container* (an FLI, a SubFLI, or a group
 * defined in Grouping & Masking) rather than onto a concrete account, the
 * system materializes a reclassification account inside that container.
 *
 * Those accounts are shown to the user as `MAN`, but `MAN` cannot be their
 * real account number: several of them can coexist in the same statement, and
 * the whole reclassification engine resolves accounts by
 * `accountNumber` (+ `name`, + `uniqueAccountId`). A shared literal would make
 * every one of them collide.
 *
 * So each gets a genuinely unique generated number and `MAN` is applied at the
 * display boundary only. Everything else — transaction refs, grouping
 * identifiers, sorting state, conflict resolution, the backend validator —
 * keeps using the real number.
 *
 * The generated number is deliberately short (12 chars). `MaskingHelper.
 * getAccountDisplayName` blanks any account number longer than 20 characters
 * (it assumes those are internal ids), which would otherwise swallow it.
 */

/** Prefix marking an account number as system-created for a container reclass. */
export const SYSTEM_RECLASS_ACCOUNT_PREFIX = 'MAN-';

/**
 * German name prefix for a system-created reclassification account. Serves as the FALLBACK when no
 * localized prefix is supplied, and is also the wording carried by every account created before
 * the name became translatable.
 */
export const SYSTEM_RECLASS_ACCOUNT_NAME_PREFIX = 'Umgliederung aus';

/** German fallback for the word joining the source to its target container (see the list below). */
export const SYSTEM_RECLASS_ACCOUNT_NAME_CONNECTOR = 'nach';

/**
 * Every localized form of the prefix, for RECOGNISING an already-generated account.
 *
 * The name is persisted and, together with its host container, decides whether a second
 * reclassification from the same source reuses the existing account or creates another one. Now
 * that the name follows the report's language, that lookup must not be language-bound: an account
 * created while the report was German has to be recognised when the same reclassification is made
 * again in English, otherwise the reuse check misses and a duplicate account appears.
 *
 * Keep in sync with `aumico_grouping_masking_reclass_generated_account_name_prefix` across the
 * locales. A missing entry only degrades reuse across a language switch — it never affects the
 * correctness of a freshly created account.
 */
/**
 * Every localized form of the word joining the source to its target CONTAINER — "Umgliederung aus X
 * NACH Gruppe Y", "… NACH Übrige kurzfristige Forderungen". Used both to BUILD the name and to
 * RECOGNISE one that already carries a container.
 *
 * Keep in sync with `aumico_grouping_masking_reclass_generated_account_name_to` across the locales,
 * and in the same spirit as the prefixes below: a missing entry only degrades recognition across a
 * language switch, never the correctness of a freshly created account.
 */
export const SYSTEM_RECLASS_ACCOUNT_NAME_CONNECTORS: readonly string[] = [
    'nach', // de / de-CH / de-DE
    'to', // en
    'vers', // fr / fr-CH
    'a', // it / it-CH
];

export const SYSTEM_RECLASS_ACCOUNT_NAME_PREFIXES: readonly string[] = [
    SYSTEM_RECLASS_ACCOUNT_NAME_PREFIX, // de / de-CH / de-DE
    'Reclassification from', // en
    'Reclassement de', // fr / fr-CH
    'Riclassificazione da', // it / it-CH
];

/**
 * Build the persisted name of a system-created reclass account.
 *
 * `prefix` is the caller's report-language wording; it falls back to German so a caller without an
 * i18n context still produces the historical name rather than a bare account name.
 */
export function systemReclassAccountName(
    sourceAccountName: string,
    prefix: string = SYSTEM_RECLASS_ACCOUNT_NAME_PREFIX,
    /**
     * Caption of the CONTAINER the account is created in — a G&M group, or the FLI / SubFLI itself.
     * It becomes part of the name ("Umgliederung aus Debitoren Inland nach Sammelgruppe"), so several
     * reclassifications from ONE source into DIFFERENT containers are told apart everywhere the
     * account appears: the target dropdown, the table, the Umgliederungsspiegel, the notes, the print.
     *
     * The name is a SNAPSHOT of the moment of creation. It is deliberately not maintained
     * afterwards: the name is load-bearing identity (transaction refs, `groupedAccounts`
     * membership and the sorting state are all keyed on `accountNumber#name`), so rewriting it
     * would mean rewriting those in lockstep. Renaming the container, or moving the account into
     * another one, therefore leaves the recorded name behind — the user renames the account
     * themselves if they want it to follow (the rename is a masked display name and touches no
     * identity).
     */
    containerCaption?: string | null,
    /** Localized "to" joining source and container; falls back to the German wording. */
    connector: string = SYSTEM_RECLASS_ACCOUNT_NAME_CONNECTOR,
): string {
    const resolved = prefix != null && prefix.trim() ? prefix.trim() : SYSTEM_RECLASS_ACCOUNT_NAME_PREFIX;
    const base = `${resolved} ${sourceAccountName ?? ''}`.trim();
    const caption = (containerCaption ?? '').trim();
    if (caption.length === 0) return base;
    const joiner = connector != null && connector.trim() ? connector.trim() : SYSTEM_RECLASS_ACCOUNT_NAME_CONNECTOR;
    return `${base} ${joiner} ${caption}`;
}

/**
 * True when `name` is the generated name for ANY of `sourceAccountNames`, in ANY supported
 * language.
 *
 * Two axes of tolerance, both needed because the name is persisted while the inputs that produced
 * it can change afterwards:
 *  - LANGUAGE — see `SYSTEM_RECLASS_ACCOUNT_NAME_PREFIXES`.
 *  - SOURCE NAME — a generated account is named after the source's DISPLAY name, so renaming the
 *    source afterwards changes what a fresh generation would produce. Callers pass both the
 *    current display name and the original, so the existing account is still recognised and
 *    reused instead of being duplicated.
 */
export function matchesSystemReclassAccountName(name: string, ...sourceAccountNames: string[]): boolean {
    const target = (name ?? '').trim();
    if (target.length === 0) return false;
    return sourceAccountNames.some((sourceName) =>
        SYSTEM_RECLASS_ACCOUNT_NAME_PREFIXES.some((prefix) => {
            const base = systemReclassAccountName(sourceName, prefix);
            if (target === base) return true;
            // A name that records its target container ("<base> nach Sammelgruppe") is still a
            // generated name for this source, so reclassifying the same source into the same
            // container reuses the account instead of creating a second one. Anchored on a KNOWN
            // connector rather than on
            // "anything after the base", so a source whose own name merely extends another's
            // ("Debitoren" vs "Debitoren Inland") cannot match.
            return SYSTEM_RECLASS_ACCOUNT_NAME_CONNECTORS.some((connector) =>
                target.startsWith(`${base} ${connector} `),
            );
        }),
    );
}


/** What the user sees instead of the generated number. */
export const SYSTEM_RECLASS_ACCOUNT_DISPLAY = 'MAN';

/** True when the number was generated by `generateSystemReclassAccountNumber`. */
export function isSystemReclassAccountNumber(accountNumber: string | null | undefined): boolean {
    return typeof accountNumber === 'string' && accountNumber.startsWith(SYSTEM_RECLASS_ACCOUNT_PREFIX);
}

/**
 * Display form of an account number: `MAN` for system-created reclass
 * accounts, the number itself for everything else.
 *
 * Use this at every user-facing boundary (tables, notes, print, PDF). Never
 * use it for lookup, comparison or persistence.
 */
export function displayAccountNumber(accountNumber: string | null | undefined): string {
    if (isSystemReclassAccountNumber(accountNumber)) return SYSTEM_RECLASS_ACCOUNT_DISPLAY;
    return accountNumber ?? '';
}

/**
 * Generate a unique `MAN-xxxxxxxx` account number.
 *
 * `existingAccountNumbers` should hold every number already present in the
 * report (both statements). The loop is a formality — an 8-hex-char slice of a
 * v4 UUID collides with vanishing probability — but the report is the identity
 * space for account numbers, so we verify rather than assume.
 */
export function generateSystemReclassAccountNumber(existingAccountNumbers: Set<string>): string {
    for (;;) {
        const candidate = SYSTEM_RECLASS_ACCOUNT_PREFIX + uuidv4().replace(/-/g, '').slice(0, 8);
        if (!existingAccountNumbers.has(candidate)) return candidate;
    }
}
